import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useEffect, useState } from "react";
import api from "@/lib/axiosInstance";
import { useDispatch } from "react-redux";
import { setCredentials } from "@/lib/redux/authSlice";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, Eye, EyeOff } from "lucide-react";
import { Spinner } from "@/components/ui/shadcn-io/spinner";

export default function Login() {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [username, setUsername] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);

    useEffect(() => {
        if (location.state?.message) {
            setError(location.state.message);
        }
        if (location.state?.successMessage) {
            setSuccess(location.state.successMessage);
        }
        if (location.state) {
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location, navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            setLoading(true);
            const formData = new FormData();
            formData.append('username', username);
            formData.append('password', password);

            const response = await api.post(`/agents/login`, formData);
            if (!response.data.status) {
                throw new Error(response.data.error);
            }

            const { agent, accessToken } = response.data;
            dispatch(setCredentials({ token: accessToken, agent }));
            navigate('/dashboard');
        } catch (err: any) {
            const errorMsg = err.response?.data?.error || err.message;
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex items-center justify-center h-screen w-screen">
            <Card className="w-[380px] shadow-xl border-0">
                <CardHeader>
                    <CardTitle className="text-center text-2xl font-bold">Hero</CardTitle>
                    <CardDescription className="text-center">Sign in to continue</CardDescription>
                </CardHeader>
                <CardContent>
                    {/* Error Alert */}
                    {error && (
                        <Alert variant="destructive" className="mb-3 bg-red-100 border-red-300">
                            <AlertCircle className="h-4 w-4" />
                            <AlertTitle>Error</AlertTitle>
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    {success && (
                        <Alert className="mb-3 bg-green-100 border-green-300">
                            <AlertCircle className="h-4 w-4" color="green" />
                            <AlertTitle>Success</AlertTitle>
                            <AlertDescription>{success}</AlertDescription>
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="you@example.com"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password">Password</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="pr-10"
                                    required
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="absolute right-0 top-0 h-full py-2 hover:bg-transparent"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-4 w-4 text-gray-400" />
                                    ) : (
                                        <Eye className="h-4 w-4 text-gray-400" />
                                    )}
                                </Button>
                            </div>
                        </div>
                        <Button 
                        type="submit" 
                        className="w-full"
                        disabled={loading}>
                            {
                                loading ? <Spinner /> :  'Sign In'
                            }
                        </Button>
                        <div className="flex justify-end mt-1">
                            <Link
                            to="/forgot-password"
                            className="text-sm text-black! hover:underline!">
                                Forgot password?
                            </Link>
                        </div>
                    </form>
                    <CardDescription className="text-center text-xs text-muted-foreground mt-3">Powered by Xpillar</CardDescription>
                </CardContent>
            </Card>
        </div>
    );
}